/**
 * Google Analytics 4.
 *
 * Loaded from here rather than hardcoded into index.html so it only ever runs
 * on the production hostname. A raw <script> tag in index.html would also fire
 * on staging.takefav.com and localhost, polluting the production property with
 * traffic that isn't real users — the same environment-isolation rule the rest
 * of this project follows for APIs and databases.
 *
 * Also gated on cookie consent (useCookieConsent): frontend/src/views/legal/
 * Cookies.vue already tells visitors GA cookies require their choice via a
 * consent banner. Call initAnalyticsIfConsented() rather than starting GA
 * directly — it is the one place that decision is checked.
 *
 * Page views are sent manually on route change (send_page_view: false) because
 * this is an SPA: GA4's automatic pageview fires once on hard load, and its
 * "enhanced measurement" history-change tracking would double-count alongside
 * the router hook.
 */
import type { Router } from 'vue-router'
import { getEnvironment } from './environment'
import { log } from './logger'
import { useCookieConsent } from '../composables/useCookieConsent'

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-JJNM90BZZW'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

let initialized = false
let pendingPageView: number | undefined
let lastSentPath: string | undefined

function loadGtagScript(id: string) {
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', id, {
    // We send page_view ourselves on each route change; see trackPageView().
    send_page_view: false,
  })
}

/**
 * Send one page_view for `path`.
 *
 * Debounced and de-duplicated: router.afterEach can settle more than once for
 * a single user-visible navigation (redirects, the URL-normalisation guard in
 * main.ts), and firing per callback would inflate pageview counts.
 */
function schedulePageView(path: string) {
  if (!initialized) return

  if (pendingPageView !== undefined) {
    window.clearTimeout(pendingPageView)
  }

  // The title resolves asynchronously once the view's data loads, so wait
  // before reading document.title — otherwise GA records the placeholder.
  pendingPageView = window.setTimeout(() => {
    pendingPageView = undefined
    if (typeof window.gtag !== 'function') return
    if (path === lastSentPath) return
    lastSentPath = path

    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    })
  }, 700)
}

function startAnalytics(router: Router) {
  if (initialized || typeof window === 'undefined') return

  if (getEnvironment() !== 'production') {
    log.debug(`📊 GA disabled outside production (env: ${getEnvironment()})`)
    return
  }
  if (!MEASUREMENT_ID) {
    log.warn('📊 GA measurement ID missing — analytics not started')
    return
  }

  loadGtagScript(MEASUREMENT_ID)
  initialized = true
  log.info(`📊 GA initialized (${MEASUREMENT_ID})`)

  // afterEach also fires for the initial navigation on a hard load, so this
  // covers first paint too for a visitor who already consented in a previous
  // session — no separate initial-load call (an earlier version had one and
  // double-counted every entry pageview).
  router.afterEach((to) => schedulePageView(to.fullPath))

  // But if consent is granted mid-session (the banner, not a fresh load),
  // afterEach won't fire again for the page the visitor is already on —
  // send its view explicitly so accepting doesn't require a reload.
  schedulePageView(router.currentRoute.value.fullPath)
}

/**
 * Call this instead of starting GA directly — everywhere this file is used
 * from. Checks the visitor's cookie choice first; does nothing if they haven't
 * decided yet or chose "Essential Only".
 *
 * Called twice by design, both safely no-ops if consent isn't granted:
 *  - once at app boot (main.ts), so a returning visitor who already accepted
 *    doesn't have to re-accept every session
 *  - once when the banner reports a decision (CookieConsentBanner.vue), so
 *    accepting starts GA immediately without a page reload
 */
export function initAnalyticsIfConsented(router: Router) {
  const { analyticsEnabled } = useCookieConsent()
  if (analyticsEnabled.value) {
    startAnalytics(router)
  }
}
